/**
 * KeyRecoveryModalController — 키 복구 모달 4단계 플로우 컨트롤러
 *
 * 기능:
 *  - 모달 열기/닫기 (ESC 키, 오버레이 클릭 지원)
 *  - Step 1: 이메일 확인 + 인증 코드 발송 (POST /recovery/send_code)
 *  - Step 2: 6자리 인증 코드 입력 + 검증 (POST /recovery/confirm)
 *  - Step 3: 새 PIN 6자리 + 확인 입력 (POST /recovery/execute)
 *  - Step 4: 완료 — 니모닉 1회 표시 + 클립보드 복사
 *
 * 보안:
 *  - 니모닉은 JS 프라이빗 변수(#mnemonic)로만 보유, DOM 노출 금지
 *  - CSRF 토큰은 meta[name="csrf-token"]에서 읽어 모든 fetch에 적용
 *  - PIN 입력 시 마스킹 처리 (● 표시)
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // ── Stimulus targets 정의 ──────────────────────────────
  static targets = [
    "container",         // 모달 최상위 컨테이너 (display:none 토글)
    "overlay",           // 풀스크린 오버레이 배경
    "modal",             // 모달 박스 (카드)
    "step1",             // Step 1 패널 (이메일 확인)
    "step2",             // Step 2 패널 (코드 입력)
    "step3",             // Step 3 패널 (새 PIN)
    "step4",             // Step 4 패널 (완료/니모닉)
    "emailDisplay",      // 이메일 주소 표시 <p>
    "sendCodeBtn",       // "인증 코드 받기" 버튼
    "step1Loading",      // Step 1 로딩 스피너
    "step1Error",        // Step 1 에러 메시지
    "codeDigit",         // 인증 코드 6자리 input (6개)
    "codeValue",         // 인증 코드 hidden input
    "verifyCodeBtn",     // "코드 확인" 버튼
    "step2Loading",      // Step 2 로딩 스피너
    "step2Error",        // Step 2 에러 메시지
    "pinDigit",          // PIN 6자리 input (6개)
    "pinConfirmDigit",   // PIN 확인 6자리 input (6개)
    "pinConfirmGroup",   // PIN 확인 그룹 컨테이너 (진동 이펙트용)
    "pinValue",          // PIN hidden input
    "pinConfirmValue",   // PIN 확인 hidden input
    "executeBtn",        // "키 복구 실행" 버튼
    "step3Loading",      // Step 3 로딩 스피너
    "step3Error",        // Step 3 에러 메시지
    "mnemonicGrid",      // 니모닉 단어 그리드
    "walletAddress",     // 지갑 주소 텍스트
    "walletAddressSection", // 지갑 주소 섹션 (display 토글)
    "copyBtn",           // "클립보드에 복사" 버튼
    "copyText",          // 복사 버튼 텍스트
    "stepIndicator"      // 스텝 인디케이터 원 (4개)
  ]

  // ── Stimulus values 정의 ──────────────────────────────
  static values = {
    email: String         // 사용자 이메일 (data-key-recovery-modal-email-value)
  }

  // ── 프라이빗 상태 ────────────────────────────────────
  #mnemonic = null        // 니모닉 문자열 — JS 변수로만 보유 (DOM 노출 금지)
  #currentStep = 1        // 현재 활성 스텝 (1~4)
  #escHandler = null      // ESC 키 이벤트 핸들러 참조 (cleanup용)

  // ── 라이프사이클: connect ─────────────────────────────
  connect() {
    // ESC 키로 모달 닫기 핸들러 등록
    this.#escHandler = (e) => {
      if (e.key === "Escape") this.close()
    }
    document.addEventListener("keydown", this.#escHandler)
  }

  // ── 라이프사이클: disconnect ──────────────────────────
  disconnect() {
    // ESC 키 핸들러 해제
    if (this.#escHandler) {
      document.removeEventListener("keydown", this.#escHandler)
      this.#escHandler = null
    }
    // body 스크롤 복원
    document.body.style.overflow = ""
  }

  // ══════════════════════════════════════════════════════
  // 모달 열기/닫기
  // ══════════════════════════════════════════════════════

  /**
   * open — 모달 열기
   * Step 1로 초기화하고 이메일 표시
   */
  open() {
    // 모달 컨테이너 표시
    this.containerTarget.style.display = ""
    // body 스크롤 방지
    document.body.style.overflow = "hidden"
    // Step 1로 초기화
    this.#currentStep = 1
    this.#showStep(1)
    // 이메일 표시 (Stimulus value에서 읽기)
    if (this.hasEmailDisplayTarget) {
      this.emailDisplayTarget.textContent = this.emailValue || "이메일 정보 없음"
    }
    // 이전 상태 초기화
    this.#resetAllInputs()
    this.#mnemonic = null
  }

  /**
   * close — 모달 닫기
   * Step 4 + 니모닉이 있으면 확인 후 닫기
   */
  close() {
    // Step 4에서 니모닉이 표시된 상태라면 확인 대화상자
    if (this.#currentStep === 4 && this.#mnemonic) {
      const confirmed = confirm(
        "니모닉을 안전하게 보관하셨나요?\n이 모달을 닫으면 다시 확인할 수 없습니다."
      )
      if (!confirmed) return
    }
    // 모달 숨김
    this.containerTarget.style.display = "none"
    // body 스크롤 복원
    document.body.style.overflow = ""
    // 니모닉 메모리 정리
    this.#mnemonic = null
    // 모든 입력 초기화
    this.#resetAllInputs()
  }

  /**
   * overlayClick — 오버레이 클릭 시 닫기
   * 모달 박스 내부 클릭은 무시 (이벤트 버블링 방지)
   */
  overlayClick(event) {
    // 오버레이 자체를 클릭한 경우에만 닫기
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  // ══════════════════════════════════════════════════════
  // Step 1: 인증 코드 발송
  // ══════════════════════════════════════════════════════

  /**
   * sendCode — 이메일로 인증 코드 발송
   * POST /recovery/send_code → { success: true, message: "..." }
   */
  async sendCode() {
    // 로딩 상태 표시
    this.#setLoading(this.sendCodeBtnTarget, this.step1LoadingTarget, true)
    this.#clearError(this.step1ErrorTarget)

    try {
      const response = await fetch("/recovery/send_code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.#getCsrfToken()
        },
        body: JSON.stringify({})
      })

      const data = await response.json()

      if (data.success) {
        // 성공 → Step 2로 전환
        this.#currentStep = 2
        this.#showStep(2)
        // 첫 번째 코드 입력칸에 포커스
        this.#setupDigitInputs(this.codeDigitTargets, this.codeValueTarget, () => {
          this.#updateVerifyButtonState()
        })
        if (this.codeDigitTargets[0]) {
          this.codeDigitTargets[0].focus()
        }
      } else {
        // 실패 → 에러 메시지 표시
        this.#showError(this.step1ErrorTarget, data.error || "인증 코드 발송에 실패했습니다.")
      }
    } catch (error) {
      // 네트워크 에러
      this.#showError(this.step1ErrorTarget, "네트워크 오류가 발생했습니다. 다시 시도해주세요.")
    } finally {
      this.#setLoading(this.sendCodeBtnTarget, this.step1LoadingTarget, false)
    }
  }

  /**
   * resendCode — 인증 코드 재발송
   * sendCode와 동일하지만 Step 2에 머물며 "재발송됨" 피드백
   */
  async resendCode() {
    this.#clearError(this.step2ErrorTarget)

    try {
      const response = await fetch("/recovery/send_code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.#getCsrfToken()
        },
        body: JSON.stringify({})
      })

      const data = await response.json()

      if (data.success) {
        // 재발송 성공 피드백 (임시 메시지)
        this.step2ErrorTarget.style.display = ""
        this.step2ErrorTarget.style.background = "var(--neon-primary-08)"
        this.step2ErrorTarget.style.borderColor = "var(--neon-primary-30)"
        this.step2ErrorTarget.style.color = "var(--neon-primary)"
        this.step2ErrorTarget.textContent = "✓ 인증 코드가 재발송되었습니다"
        // 3초 후 피드백 숨김
        setTimeout(() => {
          this.step2ErrorTarget.style.display = "none"
          // 원래 에러 스타일로 복원
          this.step2ErrorTarget.style.background = "var(--semantic-danger-08)"
          this.step2ErrorTarget.style.borderColor = "var(--semantic-danger-30)"
          this.step2ErrorTarget.style.color = "var(--semantic-danger)"
        }, 3000)
      } else {
        this.#showError(this.step2ErrorTarget, data.error || "재발송에 실패했습니다.")
      }
    } catch (error) {
      this.#showError(this.step2ErrorTarget, "네트워크 오류가 발생했습니다.")
    }
  }

  // ══════════════════════════════════════════════════════
  // Step 2: 인증 코드 검증
  // ══════════════════════════════════════════════════════

  /**
   * verifyCode — 입력된 인증 코드 검증
   * POST /recovery/confirm → { success: true }
   */
  async verifyCode() {
    const code = this.codeValueTarget.value
    if (code.length !== 6) return

    // 로딩 상태 표시
    this.#setLoading(this.verifyCodeBtnTarget, this.step2LoadingTarget, true)
    this.#clearError(this.step2ErrorTarget)

    try {
      const response = await fetch("/recovery/confirm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.#getCsrfToken()
        },
        body: JSON.stringify({ code: code })
      })

      const data = await response.json()

      if (data.success) {
        // 성공 → Step 3으로 전환
        this.#currentStep = 3
        this.#showStep(3)
        // PIN 입력 digit 세팅
        this.#setupDigitInputs(this.pinDigitTargets, this.pinValueTarget, () => {
          this.#updatePinButtonState()
        }, this.pinConfirmDigitTargets)
        this.#setupDigitInputs(this.pinConfirmDigitTargets, this.pinConfirmValueTarget, () => {
          this.#updatePinButtonState()
        })
        // 첫 번째 PIN 입력칸에 포커스
        if (this.pinDigitTargets[0]) {
          this.pinDigitTargets[0].focus()
        }
      } else {
        // 실패 → 에러 메시지 표시
        this.#showError(this.step2ErrorTarget, data.error || "인증 코드가 올바르지 않습니다.")
      }
    } catch (error) {
      this.#showError(this.step2ErrorTarget, "네트워크 오류가 발생했습니다.")
    } finally {
      this.#setLoading(this.verifyCodeBtnTarget, this.step2LoadingTarget, false)
    }
  }

  // ══════════════════════════════════════════════════════
  // Step 3: 키 복구 실행
  // ══════════════════════════════════════════════════════

  /**
   * executeRecovery — 새 PIN으로 키 복구 실행
   * POST /recovery/execute → { success: true, mnemonic: "...", wallet_address: "0x..." }
   */
  async executeRecovery() {
    const pin = this.pinValueTarget.value
    const pinConfirmation = this.pinConfirmValueTarget.value

    // PIN 완성 여부 확인
    if (pin.length !== 6 || pinConfirmation.length !== 6) return

    // PIN 일치 여부 확인
    if (pin !== pinConfirmation) {
      this.#showError(this.step3ErrorTarget, "PIN이 일치하지 않습니다.")
      return
    }

    // 로딩 상태 표시
    this.#setLoading(this.executeBtnTarget, this.step3LoadingTarget, true)
    this.#clearError(this.step3ErrorTarget)

    try {
      const response = await fetch("/recovery/execute", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.#getCsrfToken()
        },
        body: JSON.stringify({
          pin: pin,
          pin_confirmation: pinConfirmation
        })
      })

      const data = await response.json()

      if (data.success) {
        // 성공 → 니모닉을 프라이빗 변수에 저장 (DOM 노출 금지)
        this.#mnemonic = data.mnemonic

        // Step 4로 전환
        this.#currentStep = 4
        this.#showStep(4)

        // 니모닉 그리드 렌더링
        if (data.mnemonic) {
          this.#renderMnemonic(data.mnemonic.split(" "))
        }

        // 지갑 주소 표시
        if (data.wallet_address && this.hasWalletAddressSectionTarget) {
          this.walletAddressSectionTarget.style.display = ""
          this.walletAddressTarget.textContent = data.wallet_address
        }
      } else {
        // 실패 → 에러 메시지 표시
        this.#showError(this.step3ErrorTarget, data.error || "키 복구에 실패했습니다.")
      }
    } catch (error) {
      this.#showError(this.step3ErrorTarget, "네트워크 오류가 발생했습니다.")
    } finally {
      this.#setLoading(this.executeBtnTarget, this.step3LoadingTarget, false)
    }
  }

  // ══════════════════════════════════════════════════════
  // Step 4: 니모닉 클립보드 복사
  // ══════════════════════════════════════════════════════

  /**
   * copyMnemonic — 니모닉을 클립보드에 복사
   * navigator.clipboard API 사용
   */
  async copyMnemonic() {
    if (!this.#mnemonic) return

    try {
      await navigator.clipboard.writeText(this.#mnemonic)
      // 복사 성공 피드백
      if (this.hasCopyTextTarget) {
        this.copyTextTarget.textContent = "✓ Copied!"
      }
      if (this.hasCopyBtnTarget) {
        this.copyBtnTarget.style.borderColor = "var(--neon-primary)"
        this.copyBtnTarget.style.color = "var(--neon-primary)"
      }
      // 2초 후 원래 텍스트로 복원
      setTimeout(() => {
        if (this.hasCopyTextTarget) {
          this.copyTextTarget.textContent = "클립보드에 복사"
        }
        if (this.hasCopyBtnTarget) {
          this.copyBtnTarget.style.borderColor = ""
          this.copyBtnTarget.style.color = ""
        }
      }, 2000)
    } catch (error) {
      // 클립보드 API 실패 시 fallback
      console.error("클립보드 복사 실패:", error)
    }
  }

  // ══════════════════════════════════════════════════════
  // 프라이빗 헬퍼 메서드
  // ══════════════════════════════════════════════════════

  /**
   * #getCsrfToken — CSRF 토큰 가져오기
   * Rails의 csrf_meta_tags에서 생성된 meta 태그에서 읽음
   */
  #getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  /**
   * #showStep — 지정된 스텝만 표시, 나머지 숨김
   * @param {number} n — 표시할 스텝 번호 (1~4)
   */
  #showStep(n) {
    // 모든 스텝 패널 숨김
    const steps = [this.step1Target, this.step2Target, this.step3Target, this.step4Target]
    steps.forEach((step, i) => {
      step.style.display = (i + 1 === n) ? "" : "none"
    })

    // 스텝 인디케이터 업데이트
    this.stepIndicatorTargets.forEach((indicator) => {
      const stepNum = parseInt(indicator.dataset.step)
      if (stepNum === n) {
        // 현재 스텝 — 강조 (var(--semantic-warning))
        indicator.style.background = "rgba(255,165,0,0.15)"
        indicator.style.border = "2px solid var(--semantic-warning)"
        indicator.style.color = "var(--semantic-warning)"
      } else if (stepNum < n) {
        // 완료된 스텝 — 네온그린
        indicator.style.background = "var(--neon-primary-15)"
        indicator.style.border = "2px solid var(--neon-primary)"
        indicator.style.color = "var(--neon-primary)"
      } else {
        // 미래 스텝 — 비활성
        indicator.style.background = "rgba(255,255,255,0.03)"
        indicator.style.border = "1px solid rgba(255,255,255,0.1)"
        indicator.style.color = "var(--text-tertiary)"
      }
    })
  }

  /**
   * #setLoading — 버튼 로딩 상태 토글
   * @param {HTMLElement} btn — 토글할 버튼
   * @param {HTMLElement} spinner — 로딩 스피너 div
   * @param {boolean} loading — 로딩 상태 여부
   */
  #setLoading(btn, spinner, loading) {
    if (loading) {
      btn.style.display = "none"
      spinner.style.display = ""
    } else {
      btn.style.display = ""
      spinner.style.display = "none"
    }
  }

  /**
   * #showError — 에러 메시지 표시
   * @param {HTMLElement} target — 에러 메시지 div
   * @param {string} msg — 표시할 에러 메시지
   */
  #showError(target, msg) {
    target.textContent = msg
    target.style.display = ""
  }

  /**
   * #clearError — 에러 메시지 숨김
   * @param {HTMLElement} target — 에러 메시지 div
   */
  #clearError(target) {
    target.textContent = ""
    target.style.display = "none"
  }

  /**
   * #setupDigitInputs — digit-input 자동 포커스 설정
   * 숫자 입력 시 다음 칸으로 이동, 백스페이스 시 이전 칸 이동, 붙여넣기 지원
   *
   * @param {HTMLElement[]} digits — 6자리 input 엘리먼트 배열
   * @param {HTMLElement} hiddenInput — 전체 값 저장용 hidden input
   * @param {Function} onUpdate — 값 변경 시 콜백 (버튼 상태 업데이트 등)
   * @param {HTMLElement[]} [nextGroup] — 마지막 칸 입력 후 포커스할 다음 그룹 (선택사항)
   */
  #setupDigitInputs(digits, hiddenInput, onUpdate, nextGroup = null) {
    digits.forEach((digit, idx) => {
      // 기존 이벤트 리스너 제거 (중복 방지)
      const newDigit = digit.cloneNode(true)
      digit.parentNode.replaceChild(newDigit, digit)

      // input 이벤트 — 숫자 입력 + 자동 포커스
      newDigit.addEventListener("input", (e) => {
        const val = e.target.value.replace(/\D/g, "")
        e.target.value = val.slice(0, 1)
        if (val) {
          e.target.classList.add("filled")
          // PIN 입력 시 마스킹 처리 (150ms 후)
          if (digit.dataset.keyRecoveryModalTarget === "pinDigit" ||
              digit.dataset.keyRecoveryModalTarget === "pinConfirmDigit") {
            setTimeout(() => {
              if (e.target.value) {
                e.target.type = "password"
                e.target.style.webkitTextSecurity = "disc"
              }
            }, 150)
          }
          // 다음 칸으로 이동
          const currentDigits = this.#getRefreshedTargets(digits, digit)
          if (idx < 5 && currentDigits[idx + 1]) {
            currentDigits[idx + 1].focus()
          } else if (idx === 5 && nextGroup && nextGroup.length > 0) {
            // 마지막 칸이면 다음 그룹 첫 번째 칸으로 이동
            const refreshedNext = this.#getRefreshedTargets(nextGroup, nextGroup[0])
            if (refreshedNext[0]) refreshedNext[0].focus()
          }
        }
        this.#collectDigitValue(digits, hiddenInput)
        if (onUpdate) onUpdate()
      })

      // keydown 이벤트 — 백스페이스 처리
      newDigit.addEventListener("keydown", (e) => {
        if (e.key === "Backspace") {
          const currentDigits = this.#getRefreshedTargets(digits, digit)
          if (!e.target.value && idx > 0 && currentDigits[idx - 1]) {
            currentDigits[idx - 1].focus()
            currentDigits[idx - 1].value = ""
            currentDigits[idx - 1].type = "text"
            currentDigits[idx - 1].style.webkitTextSecurity = "none"
            currentDigits[idx - 1].classList.remove("filled")
            this.#collectDigitValue(digits, hiddenInput)
            if (onUpdate) onUpdate()
          } else {
            e.target.value = ""
            e.target.type = "text"
            e.target.style.webkitTextSecurity = "none"
            e.target.classList.remove("filled")
            this.#collectDigitValue(digits, hiddenInput)
            if (onUpdate) onUpdate()
          }
        }
        // 숫자, 백스페이스, Tab, 방향키 외 입력 차단
        if (!/^\d$/.test(e.key) && !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)) {
          e.preventDefault()
        }
      })

      // paste 이벤트 — 붙여넣기 지원
      newDigit.addEventListener("paste", (e) => {
        e.preventDefault()
        const pasted = (e.clipboardData || window.clipboardData)
          .getData("text")
          .replace(/\D/g, "")
          .slice(0, 6)
        const currentDigits = this.#getRefreshedTargets(digits, digit)
        pasted.split("").forEach((ch, i) => {
          if (currentDigits[i]) {
            currentDigits[i].value = ch
            currentDigits[i].classList.add("filled")
          }
        })
        if (pasted.length > 0 && pasted.length <= 6) {
          const focusIdx = Math.min(pasted.length, 5)
          if (currentDigits[focusIdx]) currentDigits[focusIdx].focus()
        }
        this.#collectDigitValue(digits, hiddenInput)
        if (onUpdate) onUpdate()
      })
    })
  }

  /**
   * #getRefreshedTargets — cloneNode 후 새로 교체된 DOM 노드 배열 가져오기
   * Stimulus targets는 cloneNode 후 stale 참조가 될 수 있으므로
   * 실제 DOM에서 data-index 기반으로 다시 조회
   *
   * @param {HTMLElement[]} originalTargets — 원본 targets 배열
   * @param {HTMLElement} sampleDigit — 대상 target의 data attribute 키 확인용
   * @returns {HTMLElement[]} — 새로 조회된 DOM 엘리먼트 배열
   */
  #getRefreshedTargets(originalTargets, sampleDigit) {
    // target 속성명으로 해당 타겟 이름 추출
    const targetAttr = sampleDigit.dataset.keyRecoveryModalTarget
    if (!targetAttr) return originalTargets
    // 모달 내에서 해당 target 속성을 가진 모든 엘리먼트를 index 순서로 조회
    const selector = `[data-key-recovery-modal-target="${targetAttr}"]`
    const elements = Array.from(this.element.querySelectorAll(selector))
    return elements.sort((a, b) => parseInt(a.dataset.index) - parseInt(b.dataset.index))
  }

  /**
   * #collectDigitValue — digit input들의 값을 hidden input에 수집
   * @param {HTMLElement[]} digits — 6자리 input 엘리먼트
   * @param {HTMLElement} hiddenInput — 전체 값 저장용 hidden input
   */
  #collectDigitValue(digits, hiddenInput) {
    const refreshed = this.#getRefreshedTargets(digits, digits[0])
    let value = ""
    refreshed.forEach((d) => { value += d.value })
    hiddenInput.value = value
  }

  /**
   * #updateVerifyButtonState — 코드 입력 완성 시 "코드 확인" 버튼 활성화
   */
  #updateVerifyButtonState() {
    const code = this.codeValueTarget.value
    if (code.length === 6) {
      this.verifyCodeBtnTarget.disabled = false
      this.verifyCodeBtnTarget.style.opacity = "1"
    } else {
      this.verifyCodeBtnTarget.disabled = true
      this.verifyCodeBtnTarget.style.opacity = "0.4"
    }
  }

  /**
   * #updatePinButtonState — PIN + 확인 완성 + 일치 시 "키 복구 실행" 버튼 활성화
   */
  #updatePinButtonState() {
    const pin = this.pinValueTarget.value
    const confirm = this.pinConfirmValueTarget.value

    // 에러 상태 초기화
    this.pinConfirmDigitTargets.forEach((d) => {
      // cloneNode 후에는 직접 DOM 조회 필요
      const refreshed = this.#getRefreshedTargets(this.pinConfirmDigitTargets, this.pinConfirmDigitTargets[0])
      refreshed.forEach((rd) => rd.classList.remove("error"))
    })
    this.#clearError(this.step3ErrorTarget)

    if (pin.length === 6 && confirm.length === 6) {
      if (pin === confirm) {
        // 일치 — 버튼 활성화
        this.executeBtnTarget.disabled = false
        this.executeBtnTarget.style.opacity = "1"
      } else {
        // 불일치 — 진동 이펙트 + 에러 표시
        this.executeBtnTarget.disabled = true
        this.executeBtnTarget.style.opacity = "0.3"
        this.#showError(this.step3ErrorTarget, "PIN이 일치하지 않습니다")
        // 확인 칸에 에러 스타일
        const confirmDigits = this.#getRefreshedTargets(
          this.pinConfirmDigitTargets, this.pinConfirmDigitTargets[0]
        )
        confirmDigits.forEach((d) => d.classList.add("error"))
        // 진동 이펙트
        if (this.hasPinConfirmGroupTarget) {
          this.pinConfirmGroupTarget.classList.add("modal-pin-shake")
          setTimeout(() => {
            this.pinConfirmGroupTarget.classList.remove("modal-pin-shake")
          }, 500)
        }
      }
    } else {
      // 미완성 — 버튼 비활성화
      this.executeBtnTarget.disabled = true
      this.executeBtnTarget.style.opacity = "0.3"
    }
  }

  /**
   * #renderMnemonic — 니모닉 단어 그리드 렌더링
   * 보안: 니모닉을 DOM data 속성에 노출하지 않고 textContent만 사용
   * @param {string[]} words — 니모닉 단어 배열
   */
  #renderMnemonic(words) {
    if (!this.hasMnemonicGridTarget) return

    // 기존 내용 제거
    this.mnemonicGridTarget.innerHTML = ""

    words.forEach((word, index) => {
      // 단어 카드 생성
      const card = document.createElement("div")
      card.style.cssText = `
        display:flex; align-items:center; gap:0.4rem; padding:0.5rem;
        background:rgba(10,10,10,0.6); border:1px solid var(--neon-primary-08);
        border-radius:2px;
      `
      // 번호
      const num = document.createElement("span")
      num.style.cssText = `
        font-family:'JetBrains Mono', monospace; font-size:0.65rem;
        font-weight:700; color:var(--text-tertiary); flex-shrink:0; width:1.25rem; text-align:right;
      `
      num.textContent = `${index + 1}.`

      // 단어 (textContent만 사용 — DOM 속성 노출 금지)
      const wordEl = document.createElement("span")
      wordEl.style.cssText = `
        font-family:'JetBrains Mono', monospace; font-size:0.8rem;
        font-weight:500; color:var(--text-primary);
      `
      wordEl.textContent = word

      card.appendChild(num)
      card.appendChild(wordEl)
      this.mnemonicGridTarget.appendChild(card)
    })
  }

  /**
   * #resetAllInputs — 모든 입력 필드 초기화
   * 모달 열기/닫기 시 이전 상태 정리
   */
  #resetAllInputs() {
    // 인증 코드 입력 초기화
    this.codeDigitTargets.forEach((d) => {
      d.value = ""
      d.classList.remove("filled")
      d.type = "text"
    })
    if (this.hasCodeValueTarget) this.codeValueTarget.value = ""

    // PIN 입력 초기화
    this.pinDigitTargets.forEach((d) => {
      d.value = ""
      d.classList.remove("filled")
      d.type = "text"
      d.style.webkitTextSecurity = "none"
    })
    this.pinConfirmDigitTargets.forEach((d) => {
      d.value = ""
      d.classList.remove("filled", "error")
      d.type = "text"
      d.style.webkitTextSecurity = "none"
    })
    if (this.hasPinValueTarget) this.pinValueTarget.value = ""
    if (this.hasPinConfirmValueTarget) this.pinConfirmValueTarget.value = ""

    // 버튼 초기화
    if (this.hasVerifyCodeBtnTarget) {
      this.verifyCodeBtnTarget.disabled = true
      this.verifyCodeBtnTarget.style.opacity = "0.4"
    }
    if (this.hasExecuteBtnTarget) {
      this.executeBtnTarget.disabled = true
      this.executeBtnTarget.style.opacity = "0.3"
    }

    // 에러 메시지 숨김
    if (this.hasStep1ErrorTarget) this.#clearError(this.step1ErrorTarget)
    if (this.hasStep2ErrorTarget) this.#clearError(this.step2ErrorTarget)
    if (this.hasStep3ErrorTarget) this.#clearError(this.step3ErrorTarget)

    // 로딩 스피너 숨김
    if (this.hasStep1LoadingTarget) this.step1LoadingTarget.style.display = "none"
    if (this.hasStep2LoadingTarget) this.step2LoadingTarget.style.display = "none"
    if (this.hasStep3LoadingTarget) this.step3LoadingTarget.style.display = "none"

    // 니모닉 그리드 초기화
    if (this.hasMnemonicGridTarget) this.mnemonicGridTarget.innerHTML = ""

    // 지갑 주소 숨김
    if (this.hasWalletAddressSectionTarget) this.walletAddressSectionTarget.style.display = "none"

    // 복사 버튼 텍스트 복원
    if (this.hasCopyTextTarget) this.copyTextTarget.textContent = "클립보드에 복사"
    if (this.hasCopyBtnTarget) {
      this.copyBtnTarget.style.borderColor = ""
      this.copyBtnTarget.style.color = ""
    }
  }
}
